import { upcommingRenewableDays } from "constant";
import { ANNIVERSARY } from "constant";
import { BIRTHDAY } from "constant";
import { collection, getDocs, query, where } from "firebase/firestore";
import moment from "moment";
import { sendWishes } from "firebaseConfig";
import { firestore } from "firebaseConfig";
import { calculateBalance } from "utils/common";
import { isSameCalendarDayAsToday } from "utils/dashboardFilters";
import { getClientDietChart } from "services/dietchart";

/**
 * @param {{ uid: string, filterMode?: "today" | "all" }} params
 * "today" = service completion date is today. "all" = next {upcommingRenewableDays} days (legacy window).
 */
export const fetchRenewables = async ({ uid, filterMode = "today" }) => {
  try {
    const currentDate = new Date();
    const daysAhead = new Date();
    daysAhead.setDate(currentDate.getDate() + upcommingRenewableDays);

    const query_ = query(
      collection(firestore, "client"),
      where("adminId", "==", uid)
    );
    const querySnapshot = await getDocs(query_);
    const data = [];

    querySnapshot.forEach((doc) => {
      let { services, name, phoneNumber } = doc.data();

      if (services && services.length > 0) {
        services.filter((service) => {
          const completionDate = new Date(service.completionDate);
          const mComplete = moment(service.completionDate).startOf("day");
          const mToday = moment().startOf("day");

          let matches = false;
          if (filterMode === "today") {
            matches = mComplete.isSame(mToday, "day");
          } else {
            matches =
              completionDate <= daysAhead && completionDate > currentDate;
          }

          if (matches) {
            data.push({
              id: doc.id,
              clientName: name,
              phoneNumber: phoneNumber,
              completionDate: service.completionDate,
              startDate: service.startDate,
              serviceName: service.serviceName,
              dietitian: service.assignDietitian,
            });
          }
          return null;
        });
      }
    });
    return data;
  } catch (error) {
    console.error("fetchRenewables failed", { uid, filterMode, error });
    throw error;
  }
};

//for getting today's birthdays
export const fetchBirthdaysAndAnniversaries = async ({ uid }) => {
  try {
    // Get today's date
    const today = new Date();
    const month = (today.getMonth() + 1).toString().padStart(2, "0"); // Add leading zero if required
    const day = today.getDate().toString().padStart(2, "0"); //Add leading zero if required

    // Format today's date in "MM-DD" format
    const todayFormatted = `${month}-${day}`;
    let data = [];

    const q = query(
      collection(firestore, "client"),
      where("adminId", "==", uid)
    );

    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      const dob = doc.data()?.dob;
      const anniversary = doc.data()?.anniversary;

      if (dob) {
        const dobParts = dob.split("-");
        const dobMonth = dobParts[1];
        const dobDay = dobParts[2];
        const dobFormattedDate = `${dobMonth}-${dobDay}`;
        if (todayFormatted === dobFormattedDate) {
          let { name, dob, email } = doc.data();
          data.push({
            id: doc.id,
            name,
            dob,
            occasion: BIRTHDAY,
            email,
          });
        }
      }

      if (anniversary) {
        const anniversaryParts = anniversary.split("-");
        const anniversaryMonth = anniversaryParts[1];
        const anniversaryDay = anniversaryParts[2];
        const anniversaryFormattedDate = `${anniversaryMonth}-${anniversaryDay}`;
        if (todayFormatted === anniversaryFormattedDate) {
          let { name, anniversary, email } = doc.data();
          data.push({
            id: doc.id,
            name,
            anniversary,
            occasion: ANNIVERSARY,
            email,
          });
        }
      }
    });
    return data;
  } catch (e) {
    throw e;
  }
};

//for getting today's follow up leads
export const fetchFollowUpLeads = async ({ uid }) => {
  try {
    const today = new Date();
    const startOfDay = new Date(today.setHours(0, 0, 0, 0));
    const endOfDay = new Date(today.setHours(23, 59, 59, 999));

    let data = [];

    const q = query(
      collection(firestore, "lead"),
      where("adminId", "==", uid),
      where("followUpDate", ">=", startOfDay.toISOString()),
      where("followUpDate", "<=", endOfDay.toISOString())
    );

    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      let { clientName, phoneNumber, status } = doc.data();
      data.push({ id: doc.id, clientName, phoneNumber, status });
    });
    return data;
  } catch (e) {
    throw e;
  }
};

/**
 * @param {{ uid: string, filterMode?: "today" | "all" }} params
 * "today" = positive balance rows where client.balanceReceivableDate is today's date.
 * "all" = all positive balance rows for active clients.
 */
export const fetchBalances = async ({ uid, filterMode = "today" }) => {
  try {
    const q = query(
      collection(firestore, "client"),
      where("adminId", "==", uid)
    );

    const querySnapshot = await getDocs(q);
    let data = [];

    querySnapshot.forEach((doc) => {
      const clientData = doc.data();
      const services = clientData?.services;
      const clientStatus = clientData?.status;
      const balanceReceivableDate = clientData?.balanceReceivableDate;

      if (clientStatus !== "Active") {
        return;
      }
      if (services && services.length > 0) {
        services.map((service) => {
          const balance = calculateBalance(
            service.totalAmount,
            service.discount,
            service.amountPaid
          );
          if (balance > 0) {
            if (
              filterMode === "today" &&
              !isSameCalendarDayAsToday(balanceReceivableDate)
            ) {
              return service;
            }
            data.push({
              name: doc.data()?.name,
              clientId: doc?.id,
              phoneNumber: doc.data()?.phoneNumber,
              balance,
              startDate: service?.startDate,
            });
          }
          return service;
        });
      }
    });

    return data;
  } catch (e) {
    console.error("fetchBalances failed", { uid, filterMode, error: e });
    throw e;
  }
};

//for getting today's Anniversary
export const fetchAnniversary = async ({ uid }) => {
  try {
    // Get today's date
    const today = new Date();
    const month = (today.getMonth() + 1).toString().padStart(2, "0"); // Add leading zero if required
    const day = today.getDate().toString().padStart(2, "0"); // Add leading zero if required

    // Format today's date in "MM-DD" format
    const todayFormatted = `${month}-${day}`;
    let data = [];

    const q = query(
      collection(firestore, "client"),
      where("adminId", "==", uid)
    );

    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      const anniversary = doc.data()?.anniversary;
      if (anniversary) {
        const parts = anniversary.split("-");
        const month = parts[1];
        const day = parts[2];
        const formattedDate = `${month}-${day}`;
        if (todayFormatted === formattedDate) {
          let { name, anniversary, email } = doc.data();
          data.push({ id: doc.id, name, anniversary, email });
        }
      }
    });
    return data;
  } catch (e) {
    throw e;
  }
};

const chunkArray = (arr, size) => {
  return arr.length > size
    ? [arr.slice(0, size), ...chunkArray(arr.slice(size), size)]
    : [arr];
};

export const fetchDietChartReminder = async ({ uid }) => {
  try {
    const data = [];
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const dayAfterTomorrow = new Date(today);
    dayAfterTomorrow.setDate(today.getDate() + 2);
    const todayFormatted = today.toISOString().split("T")[0]; // YYYY-MM-DD
    const tomorrowFormatted = tomorrow.toISOString().split("T")[0];
    const dayAfterTomorrowFormatted = dayAfterTomorrow
      .toISOString()
      .split("T")[0];

    const clientSnapshot = await getDocs(
      query(collection(firestore, "client"), where("adminId", "==", uid))
    );

    const clients = {};
    clientSnapshot.forEach((doc) => {
      const clientData = doc.data();
      const services = clientData.services || [];

      for (let service of services) {
        const start = new Date(service.startDate);
        const end = new Date(service.completionDate);
        const now = new Date();

        if (now >= start && now <= end) {
          clients[doc.id] = {
            name: clientData.name,
            assignDietitian: service.assignDietitian || null,
            [todayFormatted]: false,
            [tomorrowFormatted]: false,
            [dayAfterTomorrowFormatted]: false,
          };
          break; // stop at first valid service
        }
      }
    });

    const clientIds = Object.keys(clients);

    const clientChunks = chunkArray(clientIds, 10);
    const checkDietPlans = async (targetDate) => {
      for (const chunk of clientChunks) {
        if (chunk.length === 0) continue;
        const q = query(
          collection(firestore, "dietplan"),
          where("to", "==", targetDate),
          where("clientId", "in", chunk)
        );

        const snapshot = await getDocs(q);

        snapshot.forEach((doc) => {
          const diet = doc.data();

          if (clients[diet.clientId]) {
            clients[diet.clientId][targetDate] = true;
          }
        });
      }
    };

    await checkDietPlans(todayFormatted);
    await checkDietPlans(tomorrowFormatted);
    await checkDietPlans(dayAfterTomorrowFormatted);

    Object.entries(clients).forEach(([id, client]) => {
      if (client[todayFormatted]) {
        data.push({
          id,
          name: client.name,
          assignDietitian: client.assignDietitian,
          date: todayFormatted,
        });
      }
      if (client[tomorrowFormatted]) {
        data.push({
          id,
          name: client.name,
          assignDietitian: client.assignDietitian,
          date: tomorrowFormatted,
        });
      }
      if (client[dayAfterTomorrowFormatted]) {
        data.push({
          id,
          name: client.name,
          assignDietitian: client.assignDietitian,
          date: dayAfterTomorrowFormatted,
        });
      }
    });

    return data;
  } catch (error) {
    console.error("Error in fetchDietChartReminder:", error);
    throw error;
  }
};

//for getting today's Appointments
export const fetchAppointments = async ({ uid }) => {
  try {
    // Get today's date
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get tomorrow's date
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);

    let data = [];

    const q = query(
      collection(firestore, "appointment"),
      where("adminId", "==", uid)
    );

    const querySnapshot = await getDocs(q);

    const filteredDocs = querySnapshot.docs.filter((doc) => {
      const createdAt = doc.data().appointmentDate.toDate();
      return createdAt >= today && createdAt < tomorrow;
    });

    filteredDocs.forEach((doc) => {
      let { clientName, phoneNumber, appointmentDate } = doc.data();
      data.push({ id: doc.id, clientName, phoneNumber, appointmentDate });
    });

    return data;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

//send wishes
export const sendWishesHandler = async ({
  name,
  email,
  message,
  occasion,
  adminName,
  logo,
  ccEmail,
}) => {
  try {
    // Call the Cloud Function
    const result = await sendWishes({
      clientName: name,
      clientEmail: email,
      occasion: occasion.toLowerCase(),
      message: message,
      adminName,
      logo,
      ccEmail,
    });

    return result.data;
  } catch (error) {
    console.error("Error sending wishes:", error);
    throw error;
  }
};
